import { HoldingsQuote } from '@benzinga/quotes-v3-holdings-manager';

export const numberFormatter = (num: number, digits: number, prefix = '', suffix = '') => {
  const lookup = [
    { symbol: '', value: 1 },
    { symbol: 'k', value: 1e3 },
    { symbol: 'M', value: 1e6 },
    { symbol: 'B', value: 1e9 },
    { symbol: 'T', value: 1e12 },
    { symbol: 'P', value: 1e15 },
    { symbol: 'E', value: 1e18 },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find(function (item) {
      return num >= item.value;
    });
  return item ? prefix + (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol + suffix : '-';
};

export const BzTreeMapSectorOrIndustryOptions = [
  { label: 'Sector', value: 'sector' },
  { label: 'Industry', value: 'industry' },
  { label: 'Combined ( Sector + Industry )', value: 'combined' },
  { label: 'All Symbols', value: 'allSymbols' },
];

const rgbToHex = (color: number[]): string => {
  return '#' + ((1 << 24) + (color[0] << 16) + (color[1] << 8) + color[2]).toString(16).slice(1);
};

const hexToRgb = (hex: string) => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (_m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        b: parseInt(result[3], 16),
        g: parseInt(result[2], 16),
        r: parseInt(result[1], 16),
      }
    : null;
};

const getHex = (hex1: string, hex2: string, perc: number): string => {
  const color1 = hexToRgb(hex1) ?? { b: 0, g: 0, r: 0 };
  const color2 = hexToRgb(hex2) ?? { b: 0, g: 0, r: 0 };
  const w1 = perc / 100;
  const w2 = 1 - w1;
  const rgb = [
    Math.round(color1.r * w1 + color2.r * w2),
    Math.round(color1.g * w1 + color2.g * w2),
    Math.round(color1.b * w1 + color2.b * w2),
  ];
  const hex = rgbToHex(rgb);
  return hex;
};

export const convertToReadableFormat = (value: number): string => {
  const formattedValue = formatLargeNumber(value).toFixed(2);
  return value >= 1.0e12
    ? `${formattedValue}T`
    : value >= 1.0e9
      ? `${formattedValue}B`
      : value >= 1.0e6
        ? `${formattedValue}M`
        : value >= 1.0e3
          ? `${formattedValue}K`
          : `${formattedValue}`;
};

export const formatLargeNumber = (labelValue: number): number => {
  return Number(labelValue) >= 1.0e12
    ? Math.floor((Number(labelValue) / 1.0e12) * 100) / 100
    : Number(labelValue) >= 1.0e9
      ? Math.floor((Number(labelValue) / 1.0e9) * 100) / 100
      : Number(labelValue) >= 1.0e6
        ? Math.floor((Number(labelValue) / 1.0e6) * 100) / 100
        : Number(labelValue) >= 1.0e3
          ? Math.floor((Number(labelValue) / 1.0e3) * 100) / 100
          : Math.floor(Number(labelValue));
};

export const getColor = (quotes: HoldingsQuote, color: keyof HoldingsQuote, minVal: number, maxVal: number) => {
  const val = quotes[color];
  if (typeof val === 'number') {
    if (color.includes('percent') || color.includes('Percent')) {
      let perc = val ?? minVal ?? 0;
      perc = perc * 20;
      perc = Math.abs(perc);
      perc = perc > 100 ? 100 : perc;
      return val < 0 ? getHex('#f63538', '#414554', perc) : getHex('#30cc5a', '#414554', perc);
    } else {
      let perc = val ?? minVal ?? 0;
      perc = ((perc - minVal) / (maxVal - minVal)) * 100;
      return val < 0 ? getHex('#f63538', '#414554', perc) : getHex('#30cc5a', '#414554', perc);
    }
  }
  return '#414554';
};

export const getMinMax = (quotes: HoldingsQuote[], color: keyof HoldingsQuote) => {
  const { max, min } = quotes.reduce<{ max: number; min: number }>(
    (acc, val) => {
      const v = val[color];
      if (v === undefined) {
        return acc;
      } else {
        return {
          max: ((v ?? acc.min) as number) > acc.max ? ((v ?? acc.min) as number) : acc.max,
          min: ((v ?? acc.max) as number) < acc.min ? ((v ?? acc.max) as number) : acc.min,
        };
      }
    },
    { max: -Infinity, min: Infinity },
  );
  return { max, min };
};

export const formatNumberWithSuffix = (value: number, key: string): string => {
  const updatedKey = key.includes('changePercent') ? 'changePercent' : key;
  switch (updatedKey) {
    //for price values
    case 'afterMarketPrice':
    case 'askPrice':
    case 'bidPrice':
    case 'close':
    case 'currentPrice':
    case 'currentPriceFormatted':
    case 'dayHigh':
    case 'dayLow':
    case 'dividend':
    case 'ethPrice':
    case 'fiftyDayAveragePrice':
    case 'fiftyTwoWeekHigh':
    case 'fiftyTwoWeekLow':
    case 'forwardPE':
    case 'gainDollar':
    case 'high':
    case 'lastTradePrice':
    case 'low':
    case 'marketValue':
    case 'open':
    case 'pe':
    case 'preMarketPrice':
    case 'previousClose':
    case 'previousClosePrice':
    case 'payoutRatio':
      return `$${value}`;
    //for percentage values
    case 'changePercent':
    case 'dividendYield':
    case 'gainPercentage':
    case 'percentChange':
    case 'postToPrePercentChange':
    case 'regularHoursPercentChange':
    case 'volumePercentChange':
      return `${value.toFixed(2)}%`;
    //for volume values
    case 'marketCap': //large number for price
    case 'afterMarketVolume':
    case 'averageVolume':
    case 'currentVolume':
    case 'ethVolume':
    case 'preMarketVolume':
    case 'size':
    case 'volume':
    case 'sharesFloat':
    case 'quantity':
    case 'sharesOutstanding':
      return convertToReadableFormat(value);
    default:
      return convertToReadableFormat(value);
  }
};
